import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { renderLatex } from '@bayesprep/shared-ui/katex';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ChevronUp, Clock, CheckCircle2, XCircle, AlertCircle, MinusCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { testResultService } from '../services/testResultService';
import { TestResult } from '../types/testResult';

type QuestionFilter = 'all' | 'correct' | 'incorrect' | 'partial' | 'unanswered';

interface Option {
  id: string;
  text: string;
  hasMath: boolean;
  isCorrect: boolean;
  optionLetter: string;
}

interface QuestionResult {
  id: string;
  text: string;
  hasMath: boolean;
  type: string; // Question type: MCQ_SINGLE_CORRECT, FILL_IN_THE_BLANK, NUMERIC, etc.
  options: Option[];
  selectedOptionId: string | null;
  correctOptionId: string;
  selectedAnswer: string | null; // For FIB/Numeric questions
  correctAnswer: string | null; // For FIB/Numeric questions
  isCorrect: boolean;
  explanation?: string;
  subject: string;
  sequenceNumber: number;
}

interface DetailedTestResult extends TestResult {
  questions: QuestionResult[];
}

// Helper function to render text with LaTeX support using shared-ui
const renderTextWithLatex = (text: string): JSX.Element => {
  if (!text) return <span></span>;
  return <>{renderLatex(text)}</>;
};

const ResultsPage: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [result, setResult] = useState<DetailedTestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [questionFilter, setQuestionFilter] = useState<QuestionFilter>('all');

  useEffect(() => {
    const fetchResultAndQuestions = async () => {
      try {
        let responseData;
        let resultData;
        let questions = [];

        // First, try to get the result by ID (assuming it's a result ID)
        try {
          console.log('Attempting to fetch result by result ID:', resultId);
          responseData = await testResultService.getTestResult(resultId!);
          resultData = responseData.result;
          questions = responseData.questions || [];
          console.log('Successfully fetched result by result ID');
        } catch (error: any) {
          // If that fails with 404, try to get result by test ID (latest attempt)
          if (error.response?.status === 404) {
            console.log('Result ID not found (404), trying as test ID to get latest attempt...');
            try {
              resultData = await testResultService.getTestResultByTestId(resultId!);
              console.log('Successfully fetched latest result by test ID:', resultData.id);

              // Now get the questions using the actual result ID
              try {
                const detailedResponse = await testResultService.getTestResult(resultData.id);
                questions = detailedResponse.questions || [];
                console.log('Successfully fetched questions for result:', resultData.id);
              } catch (questionError) {
                console.warn('Could not fetch questions for result:', questionError);
                questions = [];
              }
            } catch (testIdError) {
              console.error('Failed to fetch result by test ID:', testIdError);

              // Check if this is an expired test and create a result for it
              try {
                console.log('Checking if test is expired and creating result...');
                const expiredResult = await testResultService.createExpiredTestResult(resultId!);
                resultData = expiredResult;
                questions = [];
                console.log('Created expired test result:', expiredResult.id);
              } catch (expiredError) {
                console.error('Failed to create expired test result:', expiredError);
                throw new Error('No test results found for this test. You may not have completed this test yet.');
              }
            }
          } else {
            console.error('Unexpected error fetching result:', error);
            throw error;
          }
        }

        // Process questions to create the expected format
        const processedQuestions = questions.map((question: any, index: number) => {
          const questionType = question.type || question.questionType || 'MCQ_SINGLE_CORRECT';
          const isFillInBlank = questionType === 'FILL_IN_THE_BLANK' ||
                                questionType === 'NUMERIC' ||
                                questionType === 'FILL_IN_THE_BLANK_NUMERIC';

          // Get student's answer from the answers map
          const studentAnswer = resultData.answers[question.id] || null;

          let selectedOptionId: string | null = null;
          let parsedOptions: Option[] = [];
          let correctOptionId: string | null = null;
          let isCorrect = false;

          if (isFillInBlank) {
            // For FIB/Numeric questions, compare the text answers directly
            const correctAnswer = question.correctAnswer;

            // Check if student answered and if it's correct
            if (studentAnswer !== null && studentAnswer !== undefined && studentAnswer !== '') {
              // Normalize both answers for comparison (trim whitespace, case-insensitive)
              const normalizedStudentAnswer = String(studentAnswer).trim().toLowerCase();
              const normalizedCorrectAnswer = String(correctAnswer).trim().toLowerCase();
              isCorrect = normalizedStudentAnswer === normalizedCorrectAnswer;
            }

            return {
              id: question.id,
              text: question.questionText || question.text,
              hasMath: question.hasMath || question.questionText?.includes('$') || question.text?.includes('$') || false,
              type: questionType,
              options: [], // No options for FIB/Numeric
              selectedOptionId: null,
              correctOptionId: null,
              selectedAnswer: studentAnswer,
              correctAnswer: question.correctAnswer,
              isCorrect,
              explanation: question.explanation,
              subject: question.subject || 'General',
              sequenceNumber: question.sequenceNumber || index + 1
            };
          } else {
            // For MCQ questions, use the existing logic
            selectedOptionId = studentAnswer;

            // Parse options if they are JSON strings
            if (Array.isArray(question.options)) {
              parsedOptions = question.options.map((option: any, index: number) => {
                if (typeof option === 'string') {
                  try {
                    // Try to parse JSON string
                    const parsed = JSON.parse(option);
                    return {
                      id: index.toString(),
                      text: parsed.text || option,
                      hasMath: false,
                      isCorrect: parsed.isCorrect || false,
                      optionLetter: parsed.optionLetter || String.fromCharCode(65 + index)
                    };
                  } catch (e) {
                    // If parsing fails, treat as plain text
                    return {
                      id: index.toString(),
                      text: option,
                      hasMath: false,
                      isCorrect: false,
                      optionLetter: String.fromCharCode(65 + index)
                    };
                  }
                } else {
                  // Already an object
                  return {
                    id: option.id || index.toString(),
                    text: option.text || option,
                    hasMath: option.hasMath || false,
                    isCorrect: option.isCorrect || false,
                    optionLetter: option.optionLetter || String.fromCharCode(65 + index)
                  };
                }
              });
            }

            // Find the correct option(s) based on correctAnswer field
            // correctAnswer is now in numeric format: "1", "2", "3", "4" (1-indexed)
            // For MCQ_MULTIPLE_CORRECT, correctAnswer can be "1,2,4"
            // For MCQ_SINGLE_CORRECT, correctAnswer is "1", "2", etc.
            const correctOptionIds: string[] = [];
            if (question.correctAnswer) {
              // Split by comma to handle multiple correct answers
              const correctAnswerNumbers = question.correctAnswer.split(',').map((a: string) => a.trim());

              correctAnswerNumbers.forEach((numStr: string) => {
                const correctIndex = parseInt(numStr) - 1; // Convert 1-indexed to 0-indexed (1->0, 2->1, etc.)
                if (correctIndex >= 0 && correctIndex < parsedOptions.length) {
                  correctOptionIds.push(correctIndex.toString());
                  // Mark the correct option
                  parsedOptions[correctIndex].isCorrect = true;
                }
              });
            }

            // For backward compatibility, set correctOptionId to the first correct option
            // (for single correct questions)
            correctOptionId = correctOptionIds.length > 0 ? correctOptionIds[0] : null;

            // Determine if the answer was correct
            // Both student answer and correctAnswer are now in 1-indexed numeric format (1,2,3,4)
            if (questionType === 'MCQ_MULTIPLE_CORRECT') {
              // Student answer is like "1,2,4", correctAnswer is like "1,2,4"
              const studentAnswerSet = new Set(
                (studentAnswer?.split(',') || []).map((a: string) => a.trim())
              );
              const correctAnswerSet = new Set(
                (question.correctAnswer?.split(',') || []).map((a: string) => a.trim())
              );

              // Check if sets are equal
              isCorrect = studentAnswerSet.size === correctAnswerSet.size &&
                         [...studentAnswerSet].every(a => correctAnswerSet.has(a));
            } else {
              // For single correct, compare directly
              if (selectedOptionId !== null && question.correctAnswer) {
                isCorrect = selectedOptionId.trim() === question.correctAnswer.trim();
              }
            }

            return {
              id: question.id,
              text: question.questionText || question.text,
              hasMath: question.hasMath || question.questionText?.includes('$') || question.text?.includes('$') || false,
              type: questionType,
              options: parsedOptions,
              selectedOptionId,
              correctOptionId,
              selectedAnswer: null,
              correctAnswer: null,
              isCorrect,
              explanation: question.explanation,
              subject: question.subject || 'General',
              sequenceNumber: question.sequenceNumber || index + 1
            };
          }
        });

        // Combine the result data with the processed questions
        setResult({
          ...resultData,
          questions: processedQuestions
        } as DetailedTestResult);
      } catch (error) {
        console.error('Error fetching result:', error);
        toast({
          title: 'Error',
          description: 'Failed to load test results. Please try again later.',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (resultId) {
      fetchResultAndQuestions();
    }
  }, [resultId, navigate, toast]);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[40vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
          <p className="mt-3 text-sm">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-3">Results Not Found</h2>
        <p className="text-sm text-muted-foreground mb-4">
          The test results you're looking for don't exist or have been removed.
        </p>
        <Button size="sm" onClick={() => navigate('/tests')}>Back to Tests</Button>
      </div>
    );
  }

  // Special handling for expired tests
  if (result.status === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-3">
          <Card className="border-orange-200">
            <CardHeader className="bg-orange-50 border-b border-orange-200 py-3">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-orange-800">{result.testTitle}</CardTitle>
                  <p className="text-sm text-orange-600 font-medium">Test Expired</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    This test has expired
                  </h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    The time limit for this test has passed. The test was scheduled to end on{' '}
                    <span className="font-medium">
                      {new Date(result.completedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 max-w-md mx-auto">
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">Test Details</h4>
                  <div className="space-y-1 text-xs text-gray-600">
                    <div className="flex justify-between">
                      <span>Total Questions:</span>
                      <span className="font-medium">{result.totalQuestions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Duration:</span>
                      <span className="font-medium">{result.timeLimitMinutes} minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className="font-medium text-orange-600">Expired</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/')}
                  >
                    Back to Dashboard
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate('/tests')}
                  >
                    View Other Tests
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const scorePercentage = result.percentage ? result.percentage.toFixed(2) : '0.00';
  const formattedDate = new Date(result.completedAt).toLocaleDateString();
  const formattedTime = new Date(result.completedAt).toLocaleTimeString();
  
  // Format start and end times
  const startTime = result.startedAt 
    ? new Date(result.startedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'N/A';
  
  const endTime = result.completedAt 
    ? new Date(result.completedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'N/A';

  // Helper function to determine question status
  const getQuestionStatus = (question: QuestionResult): QuestionFilter => {
    const isFillInBlank = question.type === 'FILL_IN_THE_BLANK' ||
                          question.type === 'NUMERIC' ||
                          question.type === 'FILL_IN_THE_BLANK_NUMERIC';

    // Check if not answered
    if (isFillInBlank) {
      if (!question.selectedAnswer || question.selectedAnswer === '') {
        return 'unanswered';
      }
    } else {
      if (!question.selectedOptionId) {
        return 'unanswered';
      }
    }

    // Check if correct
    if (question.isCorrect) {
      return 'correct';
    }

    // For MCQ_MULTIPLE_CORRECT, check if partially correct
    if (question.type === 'MCQ_MULTIPLE_CORRECT') {
      const studentAnswers = question.selectedOptionId?.split(',').map((a: string) => a.trim()) || [];
      const correctAnswers = question.correctAnswer?.split(',').map((a: string) => a.trim()) || [];

      // Check if there's any overlap (at least one correct answer selected)
      const hasCorrectAnswers = studentAnswers.some(ans => correctAnswers.includes(ans));

      if (hasCorrectAnswers && studentAnswers.length > 0) {
        return 'partial';
      }
    }

    // Otherwise, it's incorrect
    return 'incorrect';
  };

  // Group questions by subject
  const questionsBySubject = result.questions.reduce((acc, question) => {
    const subject = question.subject || 'General';
    if (!acc[subject]) {
      acc[subject] = [];
    }
    acc[subject].push(question);
    return acc;
  }, {} as Record<string, QuestionResult[]>);

  // Sort questions within each subject by sequence number
  Object.keys(questionsBySubject).forEach(subject => {
    questionsBySubject[subject].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  });

  const subjects = Object.keys(questionsBySubject).sort();

  // Calculate counts for each filter
  const filterCounts = {
    all: result.questions.length,
    correct: result.questions.filter(q => getQuestionStatus(q) === 'correct').length,
    incorrect: result.questions.filter(q => getQuestionStatus(q) === 'incorrect').length,
    partial: result.questions.filter(q => getQuestionStatus(q) === 'partial').length,
    unanswered: result.questions.filter(q => getQuestionStatus(q) === 'unanswered').length,
  };

  // Filter questions based on selected filter
  const getFilteredQuestions = (questions: QuestionResult[]): QuestionResult[] => {
    if (questionFilter === 'all') {
      return questions;
    }
    return questions.filter(q => getQuestionStatus(q) === questionFilter);
  };

  return (
    <div className="container mx-auto px-3 py-4 max-w-4xl">
      <div className="mb-4">
        <h1 className="text-2xl font-bold mb-1">Test Results</h1>
        <p className="text-sm text-muted-foreground">
          {result.testTitle} • {formattedDate} at {formattedTime}
        </p>
      </div>

      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Summary</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium mb-0.5">Percentage</p>
              <p className="text-2xl font-bold text-blue-900">{scorePercentage}%</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 font-medium mb-0.5">Total Marks</p>
              <p className="text-2xl font-bold text-green-900">{result.score} / {result.maxScore}</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 font-medium mb-0.5">Correct Answers</p>
              <p className="text-2xl font-bold text-purple-900">{result.correctAnswers} / {result.totalQuestions}</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-xs text-orange-700 font-medium mb-0.5">Time Taken</p>
              <p className="text-2xl font-bold text-orange-900">{result.durationMinutes} min</p>
            </div>
          </div>
          
          {/* Test Start and End Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-700 font-medium mb-0.5">Test Started</p>
                  <p className="text-sm font-semibold text-blue-900">{startTime}</p>
                </div>
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-700 font-medium mb-0.5">Test Completed</p>
                  <p className="text-sm font-semibold text-green-900">{endTime}</p>
                </div>
                <Clock className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mb-3">
        <h2 className="text-lg font-semibold mb-2">Detailed Results</h2>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={questionFilter === 'all' ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1.5 text-xs"
            onClick={() => setQuestionFilter('all')}
          >
            All Questions ({filterCounts.all})
          </Badge>

          <Badge
            variant={questionFilter === 'correct' ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1.5 text-xs bg-green-100 text-green-800 hover:bg-green-200 border-green-300"
            onClick={() => setQuestionFilter('correct')}
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Correct ({filterCounts.correct})
          </Badge>

          <Badge
            variant={questionFilter === 'incorrect' ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1.5 text-xs bg-red-100 text-red-800 hover:bg-red-200 border-red-300"
            onClick={() => setQuestionFilter('incorrect')}
          >
            <XCircle className="h-3 w-3 mr-1" />
            Incorrect ({filterCounts.incorrect})
          </Badge>

          {filterCounts.partial > 0 && (
            <Badge
              variant={questionFilter === 'partial' ? 'default' : 'outline'}
              className="cursor-pointer px-3 py-1.5 text-xs bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-300"
              onClick={() => setQuestionFilter('partial')}
            >
              <AlertCircle className="h-3 w-3 mr-1" />
              Partially Correct ({filterCounts.partial})
            </Badge>
          )}

          <Badge
            variant={questionFilter === 'unanswered' ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1.5 text-xs bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300"
            onClick={() => setQuestionFilter('unanswered')}
          >
            <MinusCircle className="h-3 w-3 mr-1" />
            Not Answered ({filterCounts.unanswered})
          </Badge>
        </div>
      </div>

      <Tabs defaultValue={subjects[0]} className="w-full">
        <TabsList className="grid w-full grid-cols-auto mb-3" style={{ gridTemplateColumns: `repeat(${subjects.length}, minmax(0, 1fr))` }}>
          {subjects.map((subject) => (
            <TabsTrigger key={subject} value={subject} className="text-xs py-1.5">
              {subject} ({getFilteredQuestions(questionsBySubject[subject]).length})
            </TabsTrigger>
          ))}
        </TabsList>

        {subjects.map((subject) => {
          const filteredQuestions = getFilteredQuestions(questionsBySubject[subject]);

          return (
          <TabsContent key={subject} value={subject} className="space-y-3">
            {filteredQuestions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No questions match the selected filter.</p>
              </div>
            ) : (
              filteredQuestions.map((question) => {
              const isFillInBlank = question.type === 'FILL_IN_THE_BLANK' ||
                                    question.type === 'NUMERIC' ||
                                    question.type === 'FILL_IN_THE_BLANK_NUMERIC';

              // Determine the status: correct, incorrect, or not answered
              let isNotAnswered = false;
              if (isFillInBlank) {
                isNotAnswered = !question.selectedAnswer || question.selectedAnswer === '';
              } else {
                isNotAnswered = !question.selectedOptionId;
              }

              const borderClass = isNotAnswered ? 'border-gray-400' : question.isCorrect ? 'border-green-500' : 'border-red-500';
              const headerClass = isNotAnswered ? 'bg-gray-50' : question.isCorrect ? 'bg-green-50' : 'bg-red-50';
              const iconClass = isNotAnswered ? 'text-gray-500' : question.isCorrect ? 'text-green-500' : 'text-red-500';
              const QuestionIcon = isNotAnswered ? MinusCircle : question.isCorrect ? CheckCircle2 : XCircle;

              return (
              <Card key={question.id} className={borderClass}>
                <CardHeader className={`${headerClass} py-2`}>
                  <CardTitle className="text-sm flex items-center">
                    <QuestionIcon className={`w-5 h-5 mr-2 ${iconClass}`} aria-hidden="true" />
                    Question {question.sequenceNumber}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3 pb-3">
                  <div className="mb-3 text-sm text-gray-900">
                    {renderTextWithLatex(question.text)}
                  </div>

                  {/* Render based on question type */}
                  {isFillInBlank ? (
                    // For FIB/Numeric questions, show the answer boxes
                    <div className="space-y-3">
                      {/* Student's Answer */}
                      {question.selectedAnswer && question.selectedAnswer !== '' ? (
                        <div className={`p-3 rounded-lg border-2 ${
                          question.isCorrect
                            ? 'bg-green-50 border-green-300'
                            : 'bg-red-50 border-red-300'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-xs font-bold text-gray-800 uppercase block mb-1">Your Answer:</span>
                              <span className={`text-base font-semibold ${
                                question.isCorrect ? 'text-green-800' : 'text-red-800'
                              }`}>{question.selectedAnswer}</span>
                            </div>
                            {question.isCorrect ? (
                              <CheckCircle2 className="h-6 w-6 text-green-700" />
                            ) : (
                              <XCircle className="h-6 w-6 text-red-700" />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded-lg">
                          <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-amber-700 mr-2" />
                            <span className="text-amber-900 font-bold text-sm">Question Not Answered</span>
                          </div>
                        </div>
                      )}

                      {/* Correct Answer (show prominently when wrong or not answered) */}
                      <div className={`p-3 rounded-lg border-2 ${
                        !question.isCorrect || !question.selectedAnswer || question.selectedAnswer === ''
                          ? 'bg-green-50 border-green-300'
                          : 'bg-blue-50 border-blue-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-gray-800 uppercase block mb-1">Correct Answer:</span>
                            <span className={`text-base font-semibold ${
                              !question.isCorrect || !question.selectedAnswer || question.selectedAnswer === ''
                                ? 'text-green-800'
                                : 'text-blue-800'
                            }`}>{question.correctAnswer}</span>
                          </div>
                          <CheckCircle2 className={`h-6 w-6 ${
                            !question.isCorrect || !question.selectedAnswer || question.selectedAnswer === ''
                              ? 'text-green-700'
                              : 'text-blue-700'
                          }`} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    // For MCQ questions, show the options
                    <>
                      <div className="space-y-2">
                        {question.options.map((option) => {
                          // Check if this option was selected by the student
                          // Both selectedOptionId and option comparison are now numeric (1-indexed)
                          let isSelected = false;
                          
                          if (question.type === 'MCQ_MULTIPLE_CORRECT') {
                            // For multiple correct, student answer is like "1,2,4"
                            // We need to check if this option's 1-indexed position is in the answer
                            const selectedAnswers = (question.selectedOptionId?.split(',') || [])
                              .map((a: string) => a.trim());
                            const optionIndex = question.options.indexOf(option);
                            const optionNumber = String(optionIndex + 1); // Convert 0-indexed to 1-indexed
                            isSelected = selectedAnswers.includes(optionNumber);
                          } else {
                            // For single correct, selectedOptionId is a 1-indexed number string
                            if (question.selectedOptionId !== null && question.selectedOptionId !== undefined) {
                              const optionIndex = question.options.indexOf(option);
                              const optionNumber = String(optionIndex + 1); // Convert 0-indexed to 1-indexed
                              isSelected = question.selectedOptionId.trim() === optionNumber;
                            }
                          }

                          // Determine styling based on correctness
                          const isCorrectOption = option.isCorrect;
                          const isCorrectlySelected = isSelected && isCorrectOption;
                          const isIncorrectlySelected = isSelected && !isCorrectOption;

                          // Determine background colors
                          let bgColor = 'bg-white';
                          let labelBgColor = 'bg-gray-100';
                          let labelTextColor = 'text-gray-700';

                          if (isCorrectlySelected) {
                            // Student selected correct answer
                            bgColor = 'bg-green-50';
                            labelBgColor = 'bg-green-200';
                            labelTextColor = 'text-green-900';
                          } else if (isIncorrectlySelected) {
                            // Student selected wrong answer
                            bgColor = 'bg-red-50';
                            labelBgColor = 'bg-red-200';
                            labelTextColor = 'text-red-900';
                          } else if (isCorrectOption) {
                            // Correct answer (not selected by student)
                            bgColor = 'bg-blue-50';
                            labelBgColor = 'bg-blue-200';
                            labelTextColor = 'text-blue-900';
                          }

                          return (
                            <div
                              key={option.id}
                              className={`p-2.5 rounded ${bgColor}`}
                            >
                              <div className="flex items-start">
                                <div className="flex-shrink-0 mr-2">
                                  <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold ${labelBgColor} ${labelTextColor} rounded-full`}>
                                    {option.optionLetter}
                                  </span>
                                </div>
                                <div className="flex-grow text-sm">
                                  {renderTextWithLatex(option.text)}
                                </div>
                                <div className="flex-shrink-0 ml-2">
                                  {isCorrectlySelected && (
                                    <div className="flex flex-col items-end gap-0">
                                      <CheckCircle2 className="w-4 h-4 text-green-700" aria-hidden="true" />
                                      <span className="text-[10px] font-bold text-green-700 uppercase leading-tight">Your Answer</span>
                                      <span className="text-[10px] font-bold text-green-700 uppercase leading-tight">Correct!</span>
                                    </div>
                                  )}
                                  {isIncorrectlySelected && (
                                    <div className="flex flex-col items-end gap-0">
                                      <XCircle className="w-4 h-4 text-red-700" aria-hidden="true" />
                                      <span className="text-[10px] font-bold text-red-700 uppercase leading-tight">Your Answer</span>
                                      <span className="text-[10px] font-bold text-red-700 uppercase leading-tight">Incorrect</span>
                                    </div>
                                  )}
                                  {!isSelected && isCorrectOption && (
                                    <div className="flex flex-col items-end gap-0">
                                      <CheckCircle2 className="w-4 h-4 text-blue-700" aria-hidden="true" />
                                      <span className="text-[10px] font-bold text-blue-700 uppercase leading-tight">Correct Answer</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Show "Not Answered" message if student didn't answer MCQ */}
                      {!question.selectedOptionId && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <AlertCircle className="h-4 w-4 text-amber-700 mr-2" />
                              <span className="text-amber-900 font-bold text-sm">Question Not Answered</span>
                            </div>
                            <div className="pl-6">
                              <p className="text-xs text-amber-800 mb-1">Correct Answer{question.type === 'MCQ_MULTIPLE_CORRECT' ? 's' : ''}:</p>
                              <p className="text-sm font-semibold text-amber-900">
                                {(() => {
                                  // Normalize correctAnswer for display (could be 1,2,3,4 or A,B,C,D)
                                  const normalizeForDisplay = (ans: string): string => {
                                    const num = parseInt(ans.trim());
                                    if (!isNaN(num) && num >= 1 && num <= 26) {
                                      return String.fromCharCode(65 + num - 1);
                                    }
                                    return ans.trim().toUpperCase();
                                  };
                                  
                                  if (question.correctAnswer) {
                                    return question.correctAnswer.split(',').map(a => normalizeForDisplay(a)).join(', ');
                                  }
                                  return question.options.filter(o => o.isCorrect).map(o => o.optionLetter).join(', ');
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Show answer summary for incorrect MCQ answers */}
                      {question.selectedOptionId && !question.isCorrect && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <XCircle className="h-4 w-4 text-red-700 mr-2" />
                              <span className="text-red-900 font-bold text-sm">Incorrect Answer</span>
                            </div>
                            <div className="pl-6 space-y-2">
                              <div>
                                <p className="text-xs text-red-700 mb-1">Your Answer:</p>
                                <p className="text-sm font-semibold text-red-900">
                                  {(() => {
                                    // Display student's answer as letter
                                    const normalizeForDisplay = (ans: string): string => {
                                      const num = parseInt(ans.trim());
                                      if (!isNaN(num) && num >= 1 && num <= 26) {
                                        return String.fromCharCode(65 + num - 1);
                                      } else if (!isNaN(num) && num >= 0 && num <= 25) {
                                        return String.fromCharCode(65 + num);
                                      }
                                      return ans.trim().toUpperCase();
                                    };
                                    
                                    if (question.type === 'MCQ_MULTIPLE_CORRECT') {
                                      return question.selectedOptionId.split(',').map(a => normalizeForDisplay(a)).join(', ');
                                    }
                                    return normalizeForDisplay(question.selectedOptionId);
                                  })()}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-green-700 mb-1">Correct Answer{question.type === 'MCQ_MULTIPLE_CORRECT' ? 's' : ''}:</p>
                                <p className="text-sm font-semibold text-green-900">
                                  {(() => {
                                    // Normalize correctAnswer for display
                                    const normalizeForDisplay = (ans: string): string => {
                                      const num = parseInt(ans.trim());
                                      if (!isNaN(num) && num >= 1 && num <= 26) {
                                        return String.fromCharCode(65 + num - 1);
                                      }
                                      return ans.trim().toUpperCase();
                                    };
                                    
                                    if (question.correctAnswer) {
                                      return question.correctAnswer.split(',').map(a => normalizeForDisplay(a)).join(', ');
                                    }
                                    return question.options.filter(o => o.isCorrect).map(o => o.optionLetter).join(', ');
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {question.explanation && (
                    <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded">
                      <h4 className="text-xs font-semibold text-blue-800 mb-1">Explanation:</h4>
                      <div className="text-xs text-blue-700">
                        {renderTextWithLatex(question.explanation)}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })
            )}
          </TabsContent>
          );
        })}
      </Tabs>


      <div className="flex justify-center mt-4">
        <Link to="/tests">
          <Button variant="outline" size="sm">Back to Tests</Button>
        </Link>
      </div>

      {/* Floating Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-full border border-blue-700 transition-all duration-300 z-50"
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default ResultsPage;
